// src/modules/app-views/_apps/appmulti/orders/pages/Document/tabs/SummaryTab/index.jsx
// *******************************************************************************
// IMPORTS
// *******************************************************************************
import { Card, Col, Descriptions, Row, Statistic } from 'antd';

import { useDocumentContext } from 'components/core/DocumentProvider';
import financialService from 'services/financialService';
import translateX from 'utils/translateX';

// *******************************************************************************
// GLOBAL VARIABLES
// *******************************************************************************
const STYLES = {
  statCard: { marginBottom: 16 },
  descCard: { marginBottom: 16 },
  travellerDesc: { marginBottom: 12 },
};

const money = v =>
  financialService.formatNumber(v ?? 0, { decimals: 2, locale: 'pt-BR' });

// *******************************************************************************
// MAIN COMPONENT
// *******************************************************************************
const SummaryTab = () => {
  // 1. PROPS, CONTEXTS & GLOBAL STATE
  const { data } = useDocumentContext();
  const record = data || {};
  const order = record?.totalInOrderDate;
  const parameters = record?.parametersInOrderDate || {};
  const plan = record?.__plan || {};
  const operator = record?.__operator || {};

  // 8. UI
  if (!order) return null;

  const orderMarkdown = financialService.getMarkdown(
    order.providerNetPrice,
    order.totalToPay,
  );
  const orderProfit = financialService.percentageToValue(order.totalToPay, orderMarkdown, 2);

  return (
    <>
      <Row gutter={16} style={STYLES.statCard}>
        <Col span={8}>
          <Card>
            <Statistic
              title={translateX('operator_cost')}
              value={money(order.providerNetPrice)}
            />
          </Card>
        </Col>
        <Col span={8}>
          <Card>
            <Statistic
              title={translateX('sale_price')}
              value={money(order.totalToPay)}
            />
          </Card>
        </Col>
        <Col span={8}>
          <Card>
            <Statistic
              title={translateX('profit_margin')}
              value={`${orderMarkdown}% (${money(orderProfit)})`}
            />
          </Card>
        </Col>
      </Row>

      <Card title={translateX('calculation_memory')} style={STYLES.descCard}>
        <Descriptions column={2} size="small" bordered>
          <Descriptions.Item label={translateX('operator')}>
            {operator?.name || '-'}
          </Descriptions.Item>
          <Descriptions.Item label={translateX('days')}>
            {Math.max(parameters.minDays || 0, record.days || 0)}
          </Descriptions.Item>
          <Descriptions.Item label={translateX('currency')}>
            {parameters.currency || '-'}
          </Descriptions.Item>
          <Descriptions.Item label={translateX('exchange_rate')}>
            {parameters.exchangeRate ?? '-'}
          </Descriptions.Item>
          <Descriptions.Item label={translateX('plan')}>
            {plan?.name || '-'}
          </Descriptions.Item>
          <Descriptions.Item label={translateX('daily_price')}>
            {`${parameters.dailyPrice ?? '-'} ${parameters.currency || ''}`}
          </Descriptions.Item>
          <Descriptions.Item label={translateX('price_no_discount')}>
            {money(order.providerGrossPrice)}
          </Descriptions.Item>
          <Descriptions.Item label={translateX('markdown')}>
            {`${parameters.markdown ?? 0}%`}
          </Descriptions.Item>
          <Descriptions.Item label={translateX('full_price')}>
            {money(order.resellerGrossPrice)}
          </Descriptions.Item>
          <Descriptions.Item label={translateX('discount')}>
            {`${parameters.discount ?? 0}%`}
          </Descriptions.Item>
          <Descriptions.Item label={translateX('table_price')}>
            {money(order.resellerNetPrice)}
          </Descriptions.Item>
          <Descriptions.Item label={translateX('net_price')}>
            {money(order.providerNetPrice)}
          </Descriptions.Item>
        </Descriptions>
      </Card>

      <Card title={translateX('discounts')} style={STYLES.descCard}>
        <Descriptions column={2} size="small" bordered>
          <Descriptions.Item label={translateX('adjusted_total')}>
            {money(order.totalAdjusted)}
          </Descriptions.Item>
          <Descriptions.Item label={translateX('admin_discount')}>
            {`${order?.adminDiscount?.percentage || 0}% / ${money(order?.adminDiscount?.value)}`}
          </Descriptions.Item>
          <Descriptions.Item label={translateX('admin_adjustment')}>
            {`${order?.adminAdjustment?.percentage || 0}% / ${money(order?.adminAdjustment?.value)}`}
          </Descriptions.Item>
          <Descriptions.Item label={translateX('coupon_discount')}>
            {order?.couponDiscount?.discount
              ? `${order.couponDiscount.discount}% / ${money(order.couponDiscount.discountValue)}`
              : translateX('n_a')}
          </Descriptions.Item>
          <Descriptions.Item label={translateX('payment_discount')}>
            {`${order?.paymentDiscount || 0}% / ${money(order?.paymentDiscountValue)}`}
          </Descriptions.Item>
          <Descriptions.Item label={translateX('total_to_pay')}>
            {money(order.totalToPay)}
          </Descriptions.Item>
        </Descriptions>
      </Card>

      <Card title={translateX('values_per_traveller')}>
        {(record?.travellers || []).map((t, i) => {
          const grievancePct =
            t.grievancePercentage ||
            (t.grievanceLevel === 1
              ? plan?.grievanceAdditionAge1
              : t.grievanceLevel === 2
                ? plan?.grievanceAdditionAge2
                : t.grievanceLevel === 3
                  ? plan?.grievanceAdditionAge3
                  : 0);
          const pNet = t?.valuesInOrderDate?.providerPrice;
          const rNet = financialService.markdownReverse(pNet, orderMarkdown, 2);
          return (
            <Descriptions
              key={i}
              column={2}
              size="small"
              bordered
              title={`${t.name || '-'}, ${t.ageInTravelDate ?? ''} ${translateX('years_old')}`}
              style={STYLES.travellerDesc}
            >
              <Descriptions.Item label={translateX('grievance')}>
                {t.grievanceLevel > 0
                  ? `${translateX('level')} ${t.grievanceLevel} - ${grievancePct}%`
                  : translateX('n_a')}
              </Descriptions.Item>
              <Descriptions.Item label={translateX('operator_price')}>
                {money(pNet)}
              </Descriptions.Item>
              <Descriptions.Item label={translateX('sale_price')}>
                {money(rNet)}
              </Descriptions.Item>
              <Descriptions.Item label={translateX('profit')}>
                {money(rNet - pNet)}
              </Descriptions.Item>
            </Descriptions>
          );
        })}
      </Card>
    </>
  );
};

// *******************************************************************************
// EXPORT
// *******************************************************************************
export default SummaryTab;
